// SPDX-License-Identifier: LicenseRef-Proprietary

import type {
  LegacyDecisionTree,
  LegacyTreeNode,
  LegacyNodeType,
} from './tree';
import type { TreeEdge, TreeNode } from './validation';

/**
 * JSON shape used for persisted EasyHTA decision trees.
 * Mirrors the Zustand store representation (`TreeNode`/`TreeEdge`).
 */
export interface DecisionTreeJson {
  title: string;
  rootId: string | null;
  nodes: Record<string, TreeNode>;
  edges: TreeEdge[];
  view?: unknown;
  // Optional schema version for future migrations.
  schemaVersion?: number;
}

export interface LegacyTreeNodeJson {
  id: string;
  type: LegacyNodeType | string;
  label: string;
  cost_eur?: number;
  qaly?: number;
  notes?: string;
  p?: number;
  children?: LegacyTreeNodeJson[];
}

export interface LegacyDecisionTreeJson {
  id: string;
  title: string;
  root: LegacyTreeNodeJson;
}

export const isDecisionTreeJson = (value: unknown): value is DecisionTreeJson => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const candidate = value as Partial<DecisionTreeJson>;
  return (
    typeof candidate.title === 'string' &&
    ('rootId' in candidate ? candidate.rootId === null || typeof candidate.rootId === 'string' : true) &&
    candidate.nodes !== undefined &&
    typeof candidate.nodes === 'object' &&
    Array.isArray(candidate.edges)
  );
};

export const isLegacyDecisionTreeJson = (
  value: unknown,
): value is LegacyDecisionTreeJson => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const candidate = value as Partial<LegacyDecisionTreeJson>;
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.title === 'string' &&
    typeof candidate.root === 'object' &&
    candidate.root !== null
  );
};

export const toLegacyTreeNode = (node: LegacyTreeNodeJson): LegacyTreeNode => ({
  id: node.id,
  type: (node.type as LegacyNodeType) ?? 'decision',
  label: node.label,
  p: node.p,
  cost_eur: node.cost_eur,
  qaly: node.qaly,
  notes: node.notes,
  children: node.children?.map(toLegacyTreeNode),
});

export const toLegacyDecisionTree = (
  payload: LegacyDecisionTreeJson,
): LegacyDecisionTree => ({
  id: payload.id,
  title: payload.title,
  root: toLegacyTreeNode(payload.root),
});

